import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const validateEmail = () => {
    if (!email?.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setServerError('');

    if (!validateEmail()) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
        redirectTo: `${window.location?.origin}/reset-password`,
      });

      if (error) {
        setServerError(error?.message || 'Failed to send reset email. Please try again.');
      } else {
        setIsSuccess(true);
      }
    } catch (err) {
      setServerError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/home-page" className="inline-flex items-center space-x-3 mb-6">
            <img
              src="/assets/images/ChatGPT_Image_Feb_3_2026_04_58_59_PM-1770675660365.png"
              alt="Draggin Trailers Logo"
              className="w-12 h-12 object-contain rounded-md"
            />
            <span className="font-heading font-bold text-2xl text-foreground">Draggin Trailers</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Forgot Password</h1>
          <p className="font-body text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-3 p-8">
          {isSuccess ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="MailCheck" size={32} color="var(--color-success)" strokeWidth={1.5} />
                </div>
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Check Your Email</h2>
              <p className="font-body text-sm text-muted-foreground">
                We've sent a password reset link to <span className="font-medium text-foreground">{email}</span>.
                Please check your inbox and follow the instructions.
              </p>
              <p className="font-body text-xs text-muted-foreground">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => setIsSuccess(false)}
                  className="text-primary hover:underline font-medium"
                >
                  try again
                </button>
                .
              </p>
              <div className="pt-2">
                <Link to="/login">
                  <Button variant="outline" fullWidth iconName="ArrowLeft" iconPosition="left">
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              {serverError && (
                <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                  <p className="font-body text-sm text-error flex-1">{serverError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  name="email"
                  label="Email Address"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e?.target?.value);
                    if (emailError) setEmailError('');
                    if (serverError) setServerError('');
                  }}
                  error={emailError}
                  required
                />

                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  iconName="Send"
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center space-x-1 font-body text-sm text-primary hover:underline"
                >
                  <Icon name="ArrowLeft" size={14} />
                  <span>Back to Sign In</span>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
